import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Progress, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CalendarOutlined, SearchOutlined } from "@ant-design/icons";
import {
  MaterialSummaryRow,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
} from "./materialShared";

// Material register grouped by material category: bold category rows (cost
// subtotal + % share; UOM/qty/rate/tasks blank — mixed UOMs make them
// meaningless and a DISTINCT task count can't be derived client-side) with
// their material rows nested beneath. Rank stays GLOBAL (the API's cost-DESC
// order) so rows cross-reference the Pareto/ABC chart and the Excel register.
// Categories sort by subtotal cost desc with Uncategorized forced last.
// Everything is client-side; expand/collapse is controlled (expanded by
// default on each fetch) and group keys are content-based so collapse state
// survives search typing. antd paginates the top-level category rows.

interface ItemRow extends MaterialSummaryRow {
  key: string;
  isGroup?: false;
  rank: number;
  qtyNum: number;
  rateNum: number;
  costNum: number;
}

interface CategoryGroupRow {
  key: string;
  isGroup: true;
  categoryId: number;
  categoryName: string;
  materialCount: number;
  costNum: number;
  children: ItemRow[];
}

type TableRow = ItemRow | CategoryGroupRow;

const GROUP_ROW_BG = "#f5f7fa";
const INK = "#022c57";
const UNCATEGORIZED = "Uncategorized";

const MaterialSummaryTable: React.FC<{
  materials: MaterialSummaryRow[];
  grandTotal: number;
  // Drill-down: clicking a material's task count opens the phase → activity →
  // task breakdown drawer for that material.
  onTasksClick?: (row: MaterialSummaryRow) => void;
  // Clicking the material NAME opens its month-by-month schedule instead.
  onMaterialClick?: (row: MaterialSummaryRow) => void;
}> = ({ materials, grandTotal, onTasksClick, onMaterialClick }) => {
  const [search, setSearch] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const groups = useMemo<CategoryGroupRow[]>(() => {
    const byCategory = new Map<number, CategoryGroupRow>();
    materials.forEach((m, index) => {
      const categoryId = num(m.categoryId);
      let group = byCategory.get(categoryId);
      if (!group) {
        group = {
          key: `CAT-${categoryId}`,
          isGroup: true as const,
          categoryId,
          categoryName: m.categoryName || UNCATEGORIZED,
          materialCount: 0,
          costNum: 0,
          children: [],
        };
        byCategory.set(categoryId, group);
      }
      const item: ItemRow = {
        ...m,
        key: `CAT-${categoryId}-MAT-${m.materialId ?? index}`,
        rank: index + 1,
        qtyNum: num(m.totalQuantity),
        rateNum: num(m.avgRate),
        costNum: num(m.totalCost),
      };
      group.children.push(item);
      group.materialCount += 1;
      group.costNum += item.costNum;
    });
    return Array.from(byCategory.values()).sort((a, b) => {
      if (a.categoryId === 0) return 1;
      if (b.categoryId === 0) return -1;
      return b.costNum - a.costNum;
    });
  }, [materials]);

  // Expanded by default on every fresh dataset (project/filter/refresh). The
  // deps must stay [groups] — depending on search would re-expand on typing.
  useEffect(() => {
    setExpandedRowKeys(groups.map((group) => group.key));
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((group) => {
        if (group.categoryName.toLowerCase().includes(q)) return group;
        const children = group.children.filter(
          (row) =>
            (row.materialName || "").toLowerCase().includes(q) ||
            (row.materialCode || "").toLowerCase().includes(q)
        );
        return children.length ? { ...group, children } : null;
      })
      .filter((group): group is CategoryGroupRow => group !== null);
  }, [groups, search]);

  const columns: ColumnsType<TableRow> = [
    {
      title: "#",
      key: "rank",
      width: 56,
      render: (_: any, row) =>
        row.isGroup ? null : (
          <span style={{ color: "#8c8c8c" }}>{(row as ItemRow).rank}</span>
        ),
    },
    Table.EXPAND_COLUMN,
    {
      title: "Material",
      key: "materialName",
      render: (_: any, row) => {
        if (row.isGroup) {
          return (
            <div>
              <span style={{ fontWeight: 600, color: INK }}>
                {row.categoryName}
              </span>
              <span style={{ marginLeft: 8, fontSize: 12, color: "#8c8c8c" }}>
                {row.materialCount} material{row.materialCount === 1 ? "" : "s"}
              </span>
            </div>
          );
        }
        const item = row as ItemRow;
        // materialId 0 is the "Unlinked material" bucket — it has no master
        // record, so neither drill-down can resolve it. Say so rather than
        // offering links that open an empty drawer.
        const drillable = num(item.materialId) > 0;
        const name = item.materialName || "—";
        return (
          <div>
            {onMaterialClick && drillable ? (
              <Tooltip title="Monthly schedule — quantity & cost by month">
                <Button
                  type="link"
                  size="small"
                  onClick={() => onMaterialClick(item)}
                  style={{
                    padding: 0,
                    height: "auto",
                    fontWeight: 500,
                    textAlign: "left",
                    whiteSpace: "normal",
                  }}
                >
                  {name}
                  <CalendarOutlined
                    style={{ fontSize: 12, marginLeft: 6, opacity: 0.65 }}
                  />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  drillable
                    ? undefined
                    : "Not linked to a material master — no drill-down available"
                }
              >
                <div style={{ fontWeight: 500 }}>{name}</div>
              </Tooltip>
            )}
            {item.materialCode ? (
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                {item.materialCode}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "UOM",
      key: "uomName",
      width: 90,
      render: (_: any, row) =>
        row.isGroup ? null : (row as ItemRow).uomName || "—",
    },
    {
      title: "Total Quantity",
      key: "qty",
      width: 130,
      align: "right",
      render: (_: any, row) =>
        row.isGroup ? null : (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtQty((row as ItemRow).qtyNum)}
          </span>
        ),
    },
    {
      title: "Avg Rate",
      key: "rate",
      width: 140,
      align: "right",
      render: (_: any, row) =>
        row.isGroup ? null : (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtINR((row as ItemRow).rateNum)}
          </span>
        ),
    },
    {
      title: "Total Cost",
      key: "cost",
      width: 130,
      align: "right",
      render: (_: any, row) => (
        <Tooltip title={fmtINR(row.costNum)}>
          <span
            style={{
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: row.isGroup ? INK : undefined,
              cursor: "default",
            }}
          >
            {fmtINRCompact(row.costNum)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "% Share",
      key: "share",
      width: 150,
      render: (_: any, row) => {
        const share = grandTotal > 0 ? (row.costNum / grandTotal) * 100 : 0;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Progress
              percent={Math.min(100, Math.round(share * 10) / 10)}
              size="small"
              strokeWidth={4}
              showInfo={false}
              strokeColor="#1677ff"
              style={{ flex: 1, minWidth: 60, margin: 0 }}
            />
            <span
              style={{
                fontSize: 12,
                color: row.isGroup ? INK : "#595959",
                fontWeight: row.isGroup ? 600 : undefined,
                fontVariantNumeric: "tabular-nums",
                width: 42,
                textAlign: "right",
              }}
            >
              {share.toFixed(1)}%
            </span>
          </div>
        );
      },
    },
    {
      title: "Tasks",
      key: "tasks",
      width: 80,
      align: "right",
      render: (_: any, row) => {
        if (row.isGroup) return null;
        const item = row as ItemRow;
        const value = num(item.taskCount);
        if (!onTasksClick || !(value > 0) || !(num(item.materialId) > 0))
          return value;
        return (
          <Tooltip title="View phase → activity → task breakdown">
            <Button
              type="link"
              size="small"
              onClick={() => onTasksClick(item)}
              style={{
                padding: 0,
                height: "auto",
                fontVariantNumeric: "tabular-nums",
                fontWeight: 600,
              }}
            >
              {value}
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 12,
        }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="Search material / code / category"
          style={{ width: 300, maxWidth: "100%" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ marginLeft: "auto" }}>
          <Button
            type="link"
            size="small"
            disabled={groups.length === 0}
            onClick={() => setExpandedRowKeys(groups.map((group) => group.key))}
          >
            Expand all
          </Button>
          <Button
            type="link"
            size="small"
            disabled={expandedRowKeys.length === 0}
            onClick={() => setExpandedRowKeys([])}
          >
            Collapse all
          </Button>
        </div>
      </div>
      <Table<TableRow>
        size="small"
        columns={columns}
        dataSource={filteredGroups}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
        }}
        onRow={(row) =>
          row.isGroup ? { style: { background: GROUP_ROW_BG } } : {}
        }
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} categories`,
        }}
        scroll={{ x: 900 }}
      />
    </>
  );
};

export default MaterialSummaryTable;
